/**
 * PersonaFacet — ADR-111 IA-1: an agent's Persona facet, a generic markdown
 * editor over `agents/<agent>/SOUL.md` (the fleet/role layer of the composed
 * SOUL — universal.md + this file, ADR-composed-soul-deploy-model). Reuses the
 * D2.1/D2.2 generic file read/propose (GET/POST /api/fleet/file(/propose))
 * FilesStudio's escape hatch also uses — this is just a pre-scoped, single-
 * purpose rendering of the same backend.
 *
 * The read-only lock follows the FILE, not the agent (ADR-150 D1). A core-owned
 * agent's SOUL is layer 4 of the ADR-015 composed SOUL, which has always been
 * the fleet's — so `spartan-brain` wears a "core" badge in the rail (its skills,
 * crons and profile really are core's) while its persona stays editable here.
 * `GET /api/fleet/file` classifies the exact path and returns a kind-aware
 * `core_owned`; that answer is the one this facet obeys. An agent-level flag
 * cannot make this distinction, which is why this facet no longer takes one.
 */
import { useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { Link } from 'react-router-dom'
import { api, AuthorTestsFailingError, CoreOwnedError } from '../api/client'
import type { FailingCheck, ProposeSkillResult, SoulReviewResult } from '../api/client'
import { SoulReviewPanel } from '../components/SoulReviewPanel'
import { SoulCopilotChat } from '../components/SoulCopilotChat'
import { FailingChecksPanel } from '../components/FailingChecksPanel'

function em(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

const soulPath = (agent: string) => `agents/${agent}/SOUL.md`

export function PersonaFacet({ agent, canPropose }: { agent: string; canPropose: boolean }) {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [found, setFound] = useState(false)
  // Per-PATH ownership, straight from the endpoint that classified it — not a
  // per-agent flag. Defaults to unlocked: if a response ever omits the field the
  // propose gate still refuses and surfaces the reason, whereas defaulting to
  // locked reinstates exactly the silent lockout this replaced.
  const [coreOwned, setCoreOwned] = useState(false)
  const [original, setOriginal] = useState('')
  const [draft, setDraft] = useState('')

  const [note, setNote] = useState('')
  const [proposing, setProposing] = useState(false)
  const [proposeErr, setProposeErr] = useState<string | null>(null)
  const [failingChecks, setFailingChecks] = useState<FailingCheck[]>([])
  const [result, setResult] = useState<ProposeSkillResult | null>(null)
  // ADR-150 D3/D5 — the override review, live. The propose gate runs the SAME
  // check, so showing it here is what stops a refusal being a surprise.
  const [review, setReview] = useState<SoulReviewResult | null>(null)

  useEffect(() => {
    let live = true
    setLoading(true)
    setResult(null)
    setProposeErr(null)
    setFailingChecks([])
    setNote('')
    api.getFleetFile(soulPath(agent))
      .then(f => {
        if (!live) return
        setFound(f.found)
        setCoreOwned(!!f.core_owned)
        setOriginal(f.content)
        setDraft(f.content)
      })
      .catch(e => { if (live) setLoadErr(em(e)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [agent])

  const dirty = draft !== original

  // Debounced like the workflow editor's validate: a review per keystroke is
  // noise while a paragraph is half-typed, and this reads two bundled files.
  useEffect(() => {
    if (loading || coreOwned) return
    const h = window.setTimeout(() => {
      api.reviewSoul(agent, draft)
        .then(setReview)
        // A failed review must not read as a clean one — say it plainly.
        .catch(e => setReview({
          findings: [], governed_sections: [], core_layers: [],
          unavailable: `the override review could not run (${em(e)})`,
        }))
    }, 500)
    return () => window.clearTimeout(h)
  }, [agent, draft, loading, coreOwned])

  const blocking = (review?.findings ?? []).filter(f => f.blocking)

  const propose = async () => {
    setProposing(true); setProposeErr(null); setFailingChecks([])
    try {
      const r = await api.proposeFleetFile(soulPath(agent), draft, note.trim() || undefined)
      setResult(r)
      setOriginal(draft)
    } catch (e) {
      if (e instanceof AuthorTestsFailingError) setFailingChecks(e.checks)
      else if (e instanceof CoreOwnedError) setProposeErr(e.message || 'This agent is core-owned and cannot be edited here.')
      else setProposeErr(em(e))
    } finally {
      setProposing(false)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Persona</h1>
        <p className="text-sm text-gray-500 mt-1">
          {agent}'s fleet/role-layer SOUL — composed with the universal SOUL at runtime. Changes are proposed
          for in-app review; nothing runs until you approve it.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 animate-pulse">Loading…</p>
      ) : loadErr ? (
        <p className="text-sm text-red-600">{loadErr}</p>
      ) : (
        <>
          {coreOwned && (
            <p className="text-xs text-gray-500 italic">
              {soulPath(agent)} ships from core (ADR-139) — it can only be changed through core, not per-fleet.
            </p>
          )}
          {!found && !coreOwned && (
            <p className="text-xs text-amber-700 italic">No SOUL.md found yet at {soulPath(agent)} — proposing will create it.</p>
          )}
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            disabled={coreOwned}
            spellCheck={false}
            rows={20}
            className="w-full font-mono text-xs p-3 border border-gray-200 rounded outline-none disabled:bg-gray-50 disabled:text-gray-500"
          />

          {!coreOwned && (
            <div className="space-y-2">
              {result && (
                result.improvement_warning ? (
                  <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-1">
                    <p>{result.improvement_warning}</p>
                  </div>
                ) : (
                  <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 space-y-1">
                    <p>Sent for review. Nothing runs until you approve it.</p>
                    {result.improvement_id
                      ? <Link to={`/improvements/${encodeURIComponent(result.improvement_id)}/pr`} className="font-medium underline">Review &amp; approve in Mission Control →</Link>
                      : <span>Find it under <Link to="/improvements" className="font-medium underline">Improvements</Link>.</span>}
                  </div>
                )
              )}
                  {/* ADR-150 D2 — author by chat, the parity with the workflow and
                  skill editors the owner asked for. The turn carries its own
                  review (repaired against the same check the propose gate runs),
                  so lifting it straight into the panel avoids briefly showing a
                  stale verdict while the debounced re-check catches up. */}
              <SoulCopilotChat
                agent={agent}
                draft={draft}
                onDraft={setDraft}
                onReview={setReview}
                disabled={!canPropose}
                self={user.email}
              />
              <SoulReviewPanel review={review} />
              {proposeErr && <p className="text-xs text-red-600 whitespace-pre-wrap break-words">{proposeErr}</p>}
              {failingChecks.length > 0 && <FailingChecksPanel checks={failingChecks} />}
              <div className="flex items-center gap-2">
                {canPropose ? (
                  <button type="button" disabled={proposing || !dirty || blocking.length > 0} onClick={() => void propose()}
                    className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                    title={
                      blocking.length > 0
                        ? 'This draft redefines a section core governs — see the review above (ADR-150 D4)'
                        : !dirty ? 'No changes to propose' : undefined
                    }>
                    {proposing ? 'Proposing…' : 'Propose'}
                  </button>
                ) : <span className="text-xs text-gray-500">Only a fleet owner can propose a persona change.</span>}
                <input type="text" value={note} onChange={e => setNote(e.target.value)}
                  placeholder="Optional note for the reviewer" className="flex-1 text-xs border border-gray-200 rounded px-2 py-1" />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
